import { Suspense, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n'; // also runs the i18n side-effect initialisation
import { getCodexTheme } from '@codex/theme';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ColorModeProvider, useColorMode } from './contexts/ColorModeContext';
import { ReaderFontProvider } from './contexts/ReaderFontContext';
import { TTSProvider } from './contexts/TTSContext';
import { CustomDomainProvider } from './contexts/CustomDomainContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { EarlyAccessProvider } from './contexts/EarlyAccessContext';
import { GlobalStyles } from '@codex/components/layout';
import AuthSync from './components/AuthSync';
import PostHogPageView from './components/PostHogPageView';
import GoogleAnalyticsPageView from './components/GoogleAnalyticsPageView';
import CookieConsentBanner from './components/CookieConsentBanner';
import Spinner from './components/Spinner';
import ErrorBoundary from './components/ErrorBoundary';
import PrerenderReady from './components/PrerenderReady';
import AppRoutes from './router';

import './styles/global.css';

// ─────────────────────────────────────────────────────────────────────────────
// Top-level App component.
//
// IMPORTANT: this file is ONLY imported by entry-client.tsx. entry-server.tsx
// constructs its own minimal provider tree (StaticRouter + Helmet + Theme +
// SSRProvider + AppRoutes) and deliberately skips the client-only contexts
// below — AuthProvider, WebSocketProvider, PostHog analytics, etc. — which
// would either hang waiting on the network or touch `window` at mount and
// crash the Node process.
//
// If you add a new provider here, decide whether it should also be added to
// entry-server.tsx. The rule of thumb: if the provider has a useEffect that
// fires network requests, opens websockets, reads localStorage, or touches
// third-party SDKs, keep it client-only (here). If it's purely in-memory
// state or pure computation, it's safe to add on the server too.
// ─────────────────────────────────────────────────────────────────────────────

function ThemedApp() {
  const { resolvedMode } = useColorMode();
  const theme = useMemo(() => getCodexTheme(resolvedMode), [resolvedMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles />
      <ErrorBoundary>
        <BrowserRouter>
          <CustomDomainProvider>
            <AuthProvider>
              <ToastProvider>
                {/* CookieConsentProvider must wrap PostHogPageView (which reads
                    consent) and CookieConsentBanner (which renders the UI).
                    It does not need auth, but lives inside so toast-driven
                    errors can surface if persistence fails. */}
                <CookieConsentProvider>
                  {/* EarlyAccessProvider lives inside BrowserRouter because it
                      uses useNavigate/useLocation to redirect to /link-expired
                      or /early-access-expired when the invite flow fails. It
                      wraps the route tree so Login/Register can consume the
                      gate via useRegistrationOpen(). */}
                  <EarlyAccessProvider>
                    <AuthSync />
                    {/* Captures a $pageview event on every React Router navigation.
                        Must live inside BrowserRouter to access useLocation. */}
                    <PostHogPageView />
                    {/* Fires GA4 `page_view` on every React Router navigation.
                        The gtag snippet in index.html only fires on the hard
                        page load, so without this, GA sees landing hits but
                        nothing else — which looks like "no data on users". */}
                    <GoogleAnalyticsPageView />
                    <WebSocketProvider>
                      <Suspense fallback={<Spinner />}>
                        <AppRoutes />
                      </Suspense>
                    </WebSocketProvider>
                    {/* Banner is outside <Suspense> so it remains visible even
                        during route-level code-splitting loads. */}
                    <CookieConsentBanner />
                  </EarlyAccessProvider>
                </CookieConsentProvider>
              </ToastProvider>
            </AuthProvider>
          </CustomDomainProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

// ── Root — ColorModeProvider wraps everything so the theme can react ──────────

export default function App(): JSX.Element {
  return (
    <I18nextProvider i18n={i18n}>
      <HelmetProvider>
        {/* PrerenderReady signals the post-build prerenderer that the DOM
            has mounted. No-op in real browsers. Must live inside
            HelmetProvider so Helmet has flushed its head injections by
            the time the signal fires. See components/PrerenderReady.tsx. */}
        <PrerenderReady />
        <ColorModeProvider>
          <ReaderFontProvider>
            <TTSProvider>
              <ThemedApp />
            </TTSProvider>
          </ReaderFontProvider>
        </ColorModeProvider>
      </HelmetProvider>
    </I18nextProvider>
  );
}
